import React from "react";
import Layout from "@/components/Layout";
import { useParams, Link } from "react-router-dom";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    body: `Eventom Online is an AI-powered live entertainment and ticketing platform for Bharat, operated by Eventom.

WHAT WE COLLECT
- Account details you give us: name, email, mobile, gender, date of birth, city, Instagram handle, interests, profile picture.
- Organiser KYC: brand name, GST/MSME details, Aadhaar details, bank details for payouts. Stored encrypted; visible only to you and reviewing admins.
- Transaction data: payment references from Razorpay, tickets, coupon usage, loyalty points, referral relationships.
- Usage data: pages visited, events viewed, AI Search queries, device info. Used to improve recommendations.

HOW WE USE IT
- Deliver tickets, event access, AI photo gallery links, and product features you've opted into.
- Personalise event & experience recommendations via our AI Discovery Agent.
- Send transactional emails (ticket confirmations, OTPs) and — if you opt in — marketing campaigns via Resend.
- Comply with Indian law and payment provider (Razorpay) requirements.

WHAT WE DON'T DO
- We don't sell personal data to third parties.
- We don't share KYC documents with anyone except you and our internal admin reviewers.

YOUR RIGHTS
Update profile fields anytime from /profile. Toggle marketing opt-in from your profile. Delete your account by contacting eventom.online@gmail.com — deletion is honoured within 30 days.`,
  },
  terms: {
    title: "Terms of Service",
    body: `By using Eventom you agree to these terms.

WHO YOU ARE
Attendee, Organiser, Admin, Brand or Ecosystem partner. Your role is set at signup. Impersonation is banned.

TICKETING
- Tickets are sold via Razorpay. Your card / UPI never touches our servers.
- A 3.6% platform fee is added to your ticket price and shown transparently at checkout before you pay.
- QR ticket is delivered by email and available in your Profile.

ORGANISERS
- Organisers pay a 3.6% commission on gross ticket sales (deducted from payouts).
- Organisers are responsible for the accuracy of event listings, artist claims, and refunds.
- KYC (GST + Aadhaar + Bank) must be complete before payouts.

ACCEPTABLE USE
No spam, harassment, scalping, or resale of tickets above face value. Community feed posts that violate Indian law will be removed and repeat offenders banned.

CHANGES
We may update these terms. Continued use after a change means you accept the new terms.`,
  },
  refund: {
    title: "Refund Policy",
    body: `TICKETS
- Refunds are handled by the organiser of each event. Contact the organiser directly for cancellation-of-event refunds.
- Where the organiser refunds, we return the 3.6% platform fee to the buyer as well.
- Razorpay processes refunds in 5–7 business days back to the original payment method.

PLATFORM FEE
- Platform fee is refunded when the event is cancelled by the organiser or by force majeure.
- Platform fee is not refunded for buyer-side no-shows.

COUPONS & LOYALTY POINTS
- Coupon discounts, when refunded, restore the coupon usage count.
- Loyalty points redeemed on a refunded order are restored to your balance.`,
  },
  cancellation: {
    title: "Cancellation Policy",
    body: `BY THE BUYER
- Free RSVP events: cancel anytime from your Profile.
- Ticketed events: cancellation policy is set by the organiser and displayed on the event page. Where the organiser allows self-cancellation, request via WhatsApp Sales.

BY THE ORGANISER
- If an organiser cancels an event, we notify all buyers by email and initiate refunds within 48 hours.
- Refunds include the 3.6% platform fee.

BY EVENTOM
- We reserve the right to cancel events that violate our policies (fraud, misrepresentation, illegal activity). Full refunds apply.`,
  },
};

export default function PoliciesPage() {
  const { slug } = useParams();
  const p = POLICIES[slug] || POLICIES.privacy;
  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="chip bg-lime/10 text-lime mb-3">Legal</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mb-2" data-testid="policy-title">{p.title}</h1>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted mb-6">Updated {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</div>

        <div className="mb-6 flex flex-wrap gap-2">
          {Object.entries(POLICIES).map(([k, v]) => (
            <Link key={k} to={`/policies/${k}`} data-testid={`policy-link-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest ${k === (slug || "privacy") ? "bg-lime text-ink" : "bg-white/5 text-muted hover:text-white"}`}>{v.title}</Link>
          ))}
        </div>

        <div className="glass rounded-3xl p-6 whitespace-pre-wrap text-sm text-text/85 leading-relaxed" data-testid="policy-body">{p.body}</div>
      </section>
    </Layout>
  );
}
